import React, { useState, useRef, useEffect } from 'react';

/**
 * Props interface for Dropdown component
 */
interface DropdownProps {
  /** Content to trigger the dropdown */
  trigger: React.ReactNode;
  /** Content to show in the dropdown */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Dropdown Component
 * A reusable dropdown menu component with click-outside handling
 */
export const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ trigger, children, className = '' }, ref) => {
    // State for dropdown visibility
    const [isOpen, setIsOpen] = useState(false);
    // Ref for handling click outside
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Handle clicks outside the dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={dropdownRef}>
        <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
          {trigger}
        </div>
        {isOpen && (
          <div className={`absolute z-50 mt-2 ${className}`}>
            {children}
          </div>
        )}
      </div>
    );
  }
);

// Display name for React DevTools
Dropdown.displayName = 'Dropdown'; 